<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { collection, query, where, orderBy, getDocs, limit } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PackageCheck, Loader2, Calendar, Tag } from 'lucide-vue-next'

interface VersionEntry {
  version: string
  type: 'release' | 'pre-release' | 'hotfix' | 'patch'
  notes?: string
  releasedAt: Date
}

interface AppWithVersions {
  id: string
  name: string
  currentVersion: string
  versionHistory: VersionEntry[]
}

const isLoading = ref(true)
const apps = ref<AppWithVersions[]>([])

const typeColors: Record<string, string> = {
  release: 'bg-green-500/10 text-green-600 border-green-500/20',
  'pre-release': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  hotfix: 'bg-red-500/10 text-red-600 border-red-500/20',
  patch: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
}

const typeLabels: Record<string, string> = {
  release: 'Release',
  'pre-release': 'Pre-release',
  hotfix: 'Hotfix',
  patch: 'Patch',
}

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

onMounted(async () => {
  try {
    // Fetch applications with version history
    const appsQuery = query(
      collection(db, 'applications'),
      orderBy('updatedAt', 'desc'),
      limit(10)
    )
    const snapshot = await getDocs(appsQuery)

    apps.value = snapshot.docs
      .map((doc) => {
        const data = doc.data()
        const versionHistory = (data.versionHistory || [])
          .map((v: { version: string; type: string; notes?: string; releasedAt?: { toDate?: () => Date } }) => ({
            version: v.version,
            type: v.type as VersionEntry['type'],
            notes: v.notes,
            releasedAt: v.releasedAt?.toDate?.() || new Date(),
          }))
          .sort((a: VersionEntry, b: VersionEntry) => b.releasedAt.getTime() - a.releasedAt.getTime())

        return {
          id: doc.id,
          name: data.name || 'Aplicación',
          currentVersion: data.currentVersion || '0.0.0',
          versionHistory,
        }
      })
      .filter((app) => app.versionHistory.length > 0)
  } catch (error) {
    console.error('Error loading changelog:', error)
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div>
    <div class="flex items-center gap-2 mb-6">
      <PackageCheck class="h-6 w-6 text-primary" />
      <h2 class="text-2xl font-bold">Historial de Cambios</h2>
    </div>

    <p class="text-muted-foreground mb-6">
      Todas las actualizaciones y mejoras de la plataforma.
    </p>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
    </div>

    <!-- Empty -->
    <div v-else-if="apps.length === 0" class="text-center py-12 text-muted-foreground">
      <PackageCheck class="h-12 w-12 mx-auto mb-4 opacity-50" />
      <p>No hay historial de cambios disponible</p>
    </div>

    <!-- Changelog List -->
    <div v-else class="space-y-8">
      <div v-for="app in apps" :key="app.id">
        <h3 class="font-semibold text-lg mb-4">{{ app.name }}</h3>

        <div class="space-y-4">
          <div
            v-for="(entry, index) in app.versionHistory.slice(0, 10)"
            :key="`${app.id}-${entry.version}`"
            class="relative pl-6 pb-4"
            :class="{ 'border-l-2 border-muted': index < app.versionHistory.length - 1 }"
          >
            <!-- Timeline dot -->
            <div
              class="absolute left-0 -translate-x-1/2 w-3 h-3 rounded-full bg-primary"
              :class="index === 0 ? 'ring-4 ring-primary/20' : ''"
            />

            <div class="flex items-start gap-3 flex-wrap">
              <Badge variant="outline" class="font-mono">
                v{{ entry.version }}
              </Badge>
              <Badge :class="typeColors[entry.type]" variant="outline">
                {{ typeLabels[entry.type] }}
              </Badge>
              <span class="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar class="h-3 w-3" />
                {{ formatDate(entry.releasedAt) }}
              </span>
            </div>

            <p v-if="entry.notes" class="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
              {{ entry.notes }}
            </p>
          </div>
        </div>

        <Separator class="mt-6" />
      </div>
    </div>
  </div>
</template>
